'use client';

import React, { FC, useEffect } from 'react';
import styles from './OtherSection.module.scss';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import Label from '@/src/components/ui/label/Label';
import { useInView } from 'react-intersection-observer';
import createAnimationArr from '@/src/lib/helpers/createAnimationArr';
import { ITheme } from '@/src/lib/types/themeInterface';
import NextImage from '@/src/components/ui/nextImage/NextImage';
import ShopifyStoreButton from '@/src/components/ui/shopifyButton/ShopifyStoreButton';

type TPage = {
  theme: ITheme;
};

const heading = 'More Tools for More Engagement';
const excerpt =
  'Optimize your store with conversion-centric tools, driving sales and engaging visitors right from their first click.';

const darkMode = {
  image: '/images/themes/stockist/other/dark.webp',
  imageLight: '/images/themes/stockist/other/light.webp',
  title: 'Stylish Dark Mode',
  text: 'Offer customers a modern, comfortable browsing experience with stylish, easy-to-toggle dark mode that reduces eye strain and enhances usability.',
  label: 'Dark Mode',
  tumblerId: 'tumbler-dark',
  tryMe: '/images/themes/stockist/tryme.svg',
};

const listItems = [
  {
    image: '/images/themes/stockist/other/pic-1.webp',
    title: 'Countdown Timer',
    text: 'Drive faster decisions by adding countdown timers that build FOMO and help increase your conversions.',
  },
  {
    image: '/images/themes/stockist/other/pic-2.webp',
    title: 'Before/After Slider',
    text: 'Show real-time transformations with a side-by-side comparison tool that highlights the impact of your products.',
  },
  {
    image: '/images/themes/stockist/other/pic-3.webp',
    title: 'Hotspots',
    text: 'Add interactive hotspots to highlight key details and benefits directly on product images, enhancing customer engagement and product insight.',
  },
  {
    image: '/images/themes/stockist/other/pic-4.webp',
    title: 'Free Shipping Progress',
    text: 'Motivate customers to buy more by showing progress toward free shipping with a clear, visual indicator.',
  },
  {
    image: '/images/themes/stockist/other/pic-5.webp',
    title: 'Popups',
    text: 'Grab attention instantly with popups that highlight special offers, important announcements, or key information to engage your visitors effectively.',
  },
  {
    image: '/images/themes/stockist/other/pic-6.webp',
    title: 'Ticker',
    text: 'Deliver short, impactful messages through a sleek, continuously moving ticker — ideal for announcements or limited-time deals.',
  },
];

const fillSection = {
  quote:
    'Experience the power of a high-performing theme designed to drive conversions, enhance user experience, and set your store apart from the competition',
  image: '/images/themes/stockist/other/fill.webp',
};

const OtherSection: FC<TPage> = ({ theme }) => {
  const { ref: inViewRef2, inView: inView2 } = useInView({
    triggerOnce: true,
    rootMargin: '-250px 0px 0px',
    threshold: 0,
  });

  useEffect(() => {
    function updateQuoteBackgrounds() {
      const quoteElements: NodeListOf<HTMLElement> = document.querySelectorAll(
        `.${styles.otherSection__fill}`
      );
      quoteElements.forEach((el) => {
        const span = el.querySelector(`.${styles['otherSection__fill-quote']} span`);
        if (!span) return;

        const elementTop = el.getBoundingClientRect().top + window.pageYOffset;
        const elementHeight = el.offsetHeight;
        const scrollTop = window.pageYOffset;
        const windowHeight = window.innerHeight;

        const scrollPercentage = Math.max(
          0,
          Math.min(1, (scrollTop - elementTop) / (elementHeight - windowHeight))
        );

        (span as HTMLElement).style.backgroundPosition = -scrollPercentage * 100 + '% 0';
      });
    }

    function onScrollOrResize() {
      updateQuoteBackgrounds();
    }

    updateQuoteBackgrounds();
    window.addEventListener('scroll', onScrollOrResize);
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, []);

  return (
    <section className={styles.otherSection}>
      <Wrapper>
        <div className={styles.otherSection__head} ref={inViewRef2}>
          <Label>Other important features</Label>
          <h2 className={`heading__2 ${styles.heading} ${inView2 && styles.animate__heading}`}>
            {createAnimationArr(heading, styles.word__wrapper, 0, 0.01)}
          </h2>
          <p className={`excerpt ${styles.excerpt} ${inView2 && styles.animate__excerpt}`}>
            {createAnimationArr(excerpt, styles.word__wrapper, 0.1, 0.01)}
          </p>
        </div>

        <div className={styles['otherSection__dark']}>
          <div className={styles['otherSection__dark-pic']}>
            <NextImage
              src={darkMode.image}
              alt={darkMode.title}
              fill
              sizes="(max-width: 480px) 100vw,70vw"
              loading="lazy"
            />
            <NextImage
              src={darkMode.imageLight}
              alt={darkMode.title}
              fill
              sizes="(max-width: 480px) 100vw, 70vw"
              loading="lazy"
            />
          </div>
          <div className={styles['otherSection__dark-about']}>
            <div className={styles['otherSection__dark-content']}>
              <h3 className={styles['otherSection__dark-title']}>{darkMode.title}</h3>
              <div className={styles['otherSection__dark-text']}>{darkMode.text}</div>
            </div>
            <div className={styles['otherSection__dark-btm']}>
              <div className={styles['otherSection__dark-label']}>{darkMode.label}</div>
              <div className={styles['otherSection__dark-tumbler']}>
                <input type="checkbox" id={darkMode.tumblerId} name={darkMode.tumblerId} />
                <label htmlFor={darkMode.tumblerId}>
                  <span>On</span>
                  <span>Off</span>
                </label>
              </div>
              <div className={styles['otherSection__dark-tryme']}>
                <img src={darkMode.tryMe} alt="Try me" />
              </div>
            </div>
          </div>
        </div>

        <div className={styles['otherSection__list']}>
          {listItems.map((item, idx) => (
            <div key={idx} className={styles['otherSection__item']}>
              <div className={styles['otherSection__pic']}>
                <NextImage
                  src={item.image}
                  alt={item.title}
                  fill
                  sizes="(max-width: 480px) 100vw, 35vw"
                  loading="lazy"
                />
              </div>
              <div className={styles['otherSection__about']}>
                <h3 className={styles['otherSection__title']}>{item.title}</h3>
                <div className={styles['otherSection__text']}>{item.text}</div>
              </div>
            </div>
          ))}
        </div>
      </Wrapper>

      <div className={styles['otherSection__fill']}>
        <div className={styles['otherSection__fill-sticky']}>
          <Wrapper>
            <div className={styles['otherSection__fill-about']}>
              <div className={styles['otherSection__fill-quote']}>
                <h2 className={styles['otherSection__fill-title']}>
                  <span>{fillSection.quote}</span>
                </h2>
              </div>
              <div className={styles['otherSection__fill-buttons']}>
                <ShopifyStoreButton isWhite={false} theme={theme} />
              </div>
            </div>
          </Wrapper>
          <div className={styles['otherSection__fill-pic']}>
            <NextImage
              src={fillSection.image}
              alt="FillSection"
              fill
              sizes="(max-width: 480px) 100vw, 50vw"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default OtherSection;
